import { useEffect, useRef, useCallback, useMemo } from 'react';
import useSelectedTheme from '@rsos/hooks/useSelectedTheme';
import { LIGHT_THEME } from '@rsos/theme/constants';
import { useTheme } from '@rsos/theme/styled';
import {
  TEXT_COLOR_DARK,
  TEXT_COLOR_LIGHT,
} from '../../../Map/useDeckGL/PureWireless/constants';
import { hexToRgba } from '../../../Map/useDeckGL/PureWireless/helpers';
import type {
  DeckPickingInfo,
  EsriMapInstance,
} from '../../../Map/useDeckGL/PureWireless/types';
import { usePureWirelessCore } from '../../../Map/useDeckGL/PureWireless/usePureWirelessCore';

// ─── Hook ────────────────────────────────────────────────────────────────────

export const usePureWirelessESRI = (map: EsriMapInstance) => {
  const themeName = useSelectedTheme();
  const theme = useTheme();
  const isDarkMode = themeName !== LIGHT_THEME;

  const callFloorHighlightColor = useMemo(
    () => hexToRgba(theme.colors.primaryBase, 255),
    [theme.colors.primaryBase],
  );
  const textColor = useMemo(
    () => (isDarkMode ? TEXT_COLOR_DARK : TEXT_COLOR_LIGHT),
    [isDarkMode],
  );

  const {
    isDetailMode,
    hoverInfo,
    selectedFloor,
    isVisible,
    geoJSONData,
    isLoadingGeoJSON,
    isCallInsideBuilding,
    estimatedCallFloor,
    buildingFloorsRef,
    setHoverInfo,
    handleClick,
    handleRoomClick,
    selectedRoomFeature,
  } = usePureWirelessCore({ mapProvider: 'esri' });

  const shouldHighlightFloor =
    isCallInsideBuilding && selectedFloor === estimatedCallFloor;

  // Stable refs for callbacks (avoids stale closures in deck.gl layers)
  const handleClickRef = useRef<(info: DeckPickingInfo) => boolean>(
    () => false,
  );
  const handleRoomClickRef = useRef<(info: DeckPickingInfo) => boolean>(
    () => false,
  );
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  const handleHoverRef = useRef<(info: DeckPickingInfo) => void>(() => {});

  useEffect(() => {
    handleClickRef.current = handleClick;
  }, [handleClick]);
  useEffect(() => {
    handleRoomClickRef.current = handleRoomClick;
  }, [handleRoomClick]);

  const handleHover = useCallback(
    (info: DeckPickingInfo) => {
      if (!isDetailMode && info.object) {
        const mapContainer = map?.mapInstance?.view?.container;
        let x = info.x;
        let y = info.y;
        if (mapContainer) {
          const rect = mapContainer.getBoundingClientRect();
          x = rect.left + info.x + window.scrollX;
          y = rect.top + info.y + window.scrollY;
        }
        setHoverInfo({ x, y, object: info.object });
      } else {
        setHoverInfo(null);
      }
    },
    [isDetailMode, map, setHoverInfo],
  );

  useEffect(() => {
    handleHoverRef.current = handleHover;
  }, [handleHover]);

  // Clear hover info when entering detail mode
  useEffect(() => {
    if (isDetailMode) setHoverInfo(null);
  }, [isDetailMode, setHoverInfo]);

  return {
    // Core state
    isDetailMode,
    hoverInfo,
    selectedFloor,
    isVisible,
    geoJSONData,
    isLoadingGeoJSON,
    isCallInsideBuilding,
    estimatedCallFloor,
    selectedRoomFeature,
    buildingFloorsRef,
    // ESRI-specific derived values
    isDarkMode,
    textColor,
    callFloorHighlightColor,
    shouldHighlightFloor,
    // Stable callback refs
    handleClickRef,
    handleRoomClickRef,
    handleHoverRef,
    // Setter (for hover info)
    setHoverInfo,
  };
};
